// Stage-03 "ניתוח מצב סטטוטורי" (statutory-state analysis) data layer.
//
// This stage does NOT collect sources or compute rights — it takes what the
// identification stage (chunk 2) already pulled and organizes it into the spec's
// reviewed data layer: subject groups of ExtractedFields, each with a source /
// confidence / status, plus an aggregated gaps list. The user then approves /
// edits / overrides / completes each value (chunk 03 §11–§13).
//
// Data source per the agreed approach "A": consume the chunk-2 identification
// snapshot (identification, parcelFacts, plans, buildingFile) + uploaded docs.
// Subjects with no extracted data become gaps to complete manually.

// Bump when the group/field construction changes so stale snapshots re-derive.
// Reuse is keyed by the PROPERTY (drop this prefix) so user edits are preserved
// as long as the property is unchanged — same rule as chunk 2.
import apiConfig from '../../../config/api';
import { mapPlan } from './identification';

const BASE = apiConfig.apiBaseUrl || '';

// Bumped when the builder's output structure changes (v2 = KB zone rules feed
// building lines / uses / rights). Reuse requires the snapshot's builtVersion to
// match, so a structure change regenerates once; property-keyed reuse then keeps
// the user's edits going forward.
export const STAT_VERSION = 'v25';
// The daf-meyda extraction schema this builder expects — MUST match the server's
// DAF_MEYDA_SCHEMA_V (lib/daf-meyda/structure-daf-meyda.mjs; CRA can't import
// from lib/, hence the duplicate). A snapshot whose dafMeyda carries an older
// schema must refetch it even when the snapshot itself is otherwise reusable.
export const DM_SCHEMA_V = 3;
// The statutory signature is `STAT_VERSION | <identification signature> | ...`,
// and the identification signature is itself `SIG_VERSION | <property fields>`.
// Reuse must key on the PROPERTY FIELDS ONLY — independent of both version
// prefixes — so a version bump on either side never forces re-running a
// completed stage (structural changes are gated separately by builtVersion).
// So drop the two leading version segments to get the bare property key.
export const statPropKey = (sig) => String(sig || '').split('|').slice(2).join('|');
// The bare property key for the CURRENT property (drop propertySignature's own
// SIG_VERSION prefix) — compared against statPropKey(savedSignature).
export const propWant = (propSig) => String(propSig || '').split('|').slice(1).join('|');

// ─── Database / Dataset registry (spec v4 §1, §4-10) ────────────────────────
// Every Data Field carries the three-tier source: Database (the system the agent
// queried) → Dataset (the specific layer / document / report section inside it)
// → source reference (link + page/section). These constants are the canonical
// ids/names for the databases the pilot knows; per-plan and per-document
// datasets are derived at build time.
export const DATABASES = {
  govmap: { id: 'db_govmap', name: 'GovMap (מיפוי ארצי)' },
  gis: { id: 'db_gis_tel_aviv', name: 'GIS עירוני תל אביב' },
  rightsSheet: { id: 'db_rights_sheet_tlv', name: 'דף זכויות עיריית תל אביב-יפו' },
  xplan: { id: 'db_xplan', name: 'מנהל התכנון / XPlan' },
  buildingFile: { id: 'db_tik_binyan', name: 'תיק בניין עירוני' },
  archive: { id: 'db_project_archive', name: 'ארכיון מסמכי הפרויקט' },
  tabu: { id: 'db_tabu', name: 'טאבו / נסח מקרקעין' },
  survey: { id: 'db_survey', name: 'מדידה / תכנית מצב קיים' },
  visual: { id: 'db_visual', name: 'תיעוד חזותי / Street View' },
};

// fetch with a hard timeout. A stalled request (backend restart mid-flight, a
// dropped socket, or a hung external GIS call) otherwise never settles — which
// left the statutory screen spinning forever with no escape. On timeout the
// AbortController rejects, so callers hit their catch and degrade gracefully.
function fetchT(url, opts = {}, timeoutMs = 12000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  // Also abort when the caller's signal fires (navigation / unmount), so a
  // request the screen no longer needs releases its connection immediately.
  const { signal: ext, ...rest } = opts;
  if (ext) {
    if (ext.aborted) ctrl.abort();
    else ext.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  return fetch(url, { ...rest, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// Fetch the CURRENT applicable plans for a parcel (by ITM coords) + their zone
// rules, mapped to the internal plan shape. Reflects the live KB (e.g. a
// newly-ingested plan), so the statutory stage isn't limited to the plan list
// frozen in the identification snapshot. Returns null on any failure.
// Fetch + structure the TA daf meyda (מידע מוקדם) for a parcel — the city's
// authoritative rights report. Returns the structured object or null.
export async function fetchDafMeyda(gush, helka, signal) {
  const g = String(gush || '').trim();
  const h = String(helka || '').trim();
  if (!g || !h) return null;
  try {
    // Long timeout — the first (uncached) structuring runs 20-80s server-side.
    const res = await fetchT(`${BASE}/api/ask?action=daf-meyda`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gush: g, helka: h }), signal,
    }, 95000);
    if (!res.ok) return null;
    const data = await res.json();
    return data.structured || null;
  } catch { return null; }
}

// The parcel's authoritative land use (GIS layer 837) — the single per-parcel
// zone (e.g. מגורים ב) + the plan that sets it. Used to source the land-use
// fields and to SCOPE the KB zone rules to the parcel's use category. Reaches an
// external GIS server, so it's the most likely to stall — keep the timeout tight.
export async function fetchParcelLandUse(coords, signal) {
  if (!coords || coords.x == null || coords.y == null) return null;
  try {
    const res = await fetchT(`${BASE}/api/ask?action=parcel-landuse`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ x_itm: coords.x, y_itm: coords.y }), signal,
    }, 15000);
    if (!res.ok) return null;
    const data = await res.json();
    return data.landUse || null;
  } catch { return null; }
}

export async function fetchApplicablePlans(coords, signal) {
  if (!coords || coords.x == null || coords.y == null) return null;
  try {
    const res = await fetchT(`${BASE}/api/ask?action=applicable-plans`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ x_itm: coords.x, y_itm: coords.y }), signal,
    }, 15000);
    if (!res.ok) return null;
    const data = await res.json();
    return (data.plans || []).map(mapPlan);
  } catch { return null; }
}

export const CONFIDENCE = {
  high: { label: 'גבוהה', tone: 'ok' },
  medium: { label: 'בינונית', tone: 'warn' },
  low: { label: 'נמוכה', tone: 'bad' },
  user_defined: { label: 'הזנת משתמש', tone: 'info' },
  missing: { label: '—', tone: 'neutral' },
};

// Field statuses per spec v4 §14: three distinct kinds of "no value" (the datum
// isn't in any dataset / the dataset that would carry it was never collected /
// the linked source doesn't contain it), plus user_input (manual value, no
// supporting source) as a first-class status distinct from override (user
// changed a sourced value — original + reason kept).
export const FIELD_STATUS = {
  approved: { label: 'אושר', tone: 'ok' },
  check: { label: 'ממתין לאישור', tone: 'warn' },
  override: { label: 'שונה ידנית', tone: 'info' },
  user_input: { label: 'הזנה ידנית', tone: 'info' },
  not_in_scope: { label: 'לא בסקופ', tone: 'neutral' },
  missing: { label: 'חסר', tone: 'bad' },
  missing_source: { label: 'מקור חסר', tone: 'bad' },
  not_found: { label: 'לא נמצא במקור', tone: 'warn' },
};

// A field with no value, in any of its spec flavors.
export const isGapStatus = (s) => s === 'missing' || s === 'missing_source' || s === 'not_found';

// Per-group summary counts for the closed-list row (§7.2). Only the gap statuses
// are surfaced in red in the UI; the rest are neutral tallies.
export function groupCounts(group) {
  const f = (group && group.fields) || [];
  const missing = f.filter((x) => isGapStatus(x.status)).length;
  const notInScope = f.filter((x) => x.status === 'not_in_scope').length;
  const approved = f.filter((x) => x.status === 'approved' || x.status === 'override' || x.status === 'user_input').length;
  const check = f.filter((x) => x.status === 'check').length;
  return { total: f.length, data: f.length - missing - notInScope, approved, check, missing, notInScope };
}

// Flatten every gap (missing / missing-source / not-found) across all groups
// into the central gaps list (§12).
export function collectGaps(groups) {
  const out = [];
  for (const g of groups || []) {
    for (const f of g.fields || []) {
      if (isGapStatus(f.status)) out.push({ ...f, groupTitle: g.title });
    }
  }
  return out;
}

// Canonical DATABASE name for an expectedSource string — the gaps list groups
// by the SOURCE the system couldn't reach, not by individual documents/layers
// (graphic-log §2.3/§2.5), so free-text variants must map to one name.
export function canonicalSourceName(expectedSource) {
  const s = String(expectedSource || '');
  if (/תיק בניין|נסח טאבו/.test(s)) return 'תיק בניין עירוני';
  if (/דף זכויות|תקנון תכנית הרובע|פירוט זכויות/.test(s)) return 'דף זכויות עיריית תל אביב-יפו';
  if (/GIS/.test(s)) return 'GIS עירוני תל אביב';
  if (/GovMap|פוליגון מגרש/.test(s)) return 'GovMap (מיפוי ארצי)';
  if (/XPlan|תקנון תכנית|מנהל התכנון/i.test(s)) return 'מנהל התכנון / XPlan';
  return s || 'מקור לא מזוהה';
}

// Immutable single-field update used by every user action (§11). `patch` is an
// object or a (field) => partial function.
export function updateField(groups, id, patch) {
  return (groups || []).map((g) => ({
    ...g,
    fields: (g.fields || []).map((f) => (f.id === id
      ? { ...f, ...(typeof patch === 'function' ? patch(f) : patch) }
      : f)),
  }));
}

export function toggleGroupOpen(groups, id, open) {
  return (groups || []).map((g) => (g.id === id ? { ...g, isOpen: open ?? !g.isOpen } : g));
}
export function setAllOpen(groups, open) {
  return (groups || []).map((g) => ({ ...g, isOpen: open }));
}

function logTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

// Shoelace area (m²) of an EPSG:2039 polygon (rings[0] = outer ring, in metres).
function polygonArea(rings) {
  const r = (rings && rings[0]) || [];
  if (r.length < 3) return null;
  let a = 0;
  for (let i = 0; i < r.length; i += 1) {
    const [x1, y1] = r[i];
    const [x2, y2] = r[(i + 1) % r.length];
    a += (x1 * y2) - (x2 * y1);
  }
  return Math.abs(a) / 2;
}

// Convex hull (monotone chain) — base for the oriented bounding rectangle.
function convexHull(points) {
  const pts = [...points].sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

// Minimal-area oriented bounding rectangle over the parcel ring (EPSG:2039 m).
// Returns { w, d } with w ≤ d — the geometric base for the "area between
// building lines" estimate (graphic-feedback log §5.2).
function minAreaRect(rings) {
  const r = (rings && rings[0]) || [];
  if (r.length < 3) return null;
  const hull = convexHull(r);
  if (hull.length < 3) return null;
  let best = null;
  for (let i = 0; i < hull.length; i += 1) {
    const [x1, y1] = hull[i];
    const [x2, y2] = hull[(i + 1) % hull.length];
    const len = Math.hypot(x2 - x1, y2 - y1);
    if (!len) continue;
    const ux = (x2 - x1) / len; const uy = (y2 - y1) / len;
    let minU = Infinity; let maxU = -Infinity; let minV = Infinity; let maxV = -Infinity;
    for (const [px, py] of hull) {
      const u = px * ux + py * uy;
      const v = -px * uy + py * ux;
      if (u < minU) minU = u; if (u > maxU) maxU = u;
      if (v < minV) minV = v; if (v > maxV) maxV = v;
    }
    const a = maxU - minU; const b = maxV - minV;
    if (!best || a * b < best.area) best = { area: a * b, w: Math.min(a, b), d: Math.max(a, b) };
  }
  return best ? { w: best.w, d: best.d } : null;
}

// A plan's PRIMARY role (§9.5) — what it chiefly contributes. Derived from the
// zone rules it carries, then from its type. One label, highest-priority first.
function planPrimaryRole(p) {
  const zones = p.zones || [];
  const any = (k) => zones.some((z) => z[k] != null && z[k] !== '');
  if (any('max_far_pct')) return 'מקור זכויות';
  if (any('setback_front_m') || any('setback_side_m') || any('setback_rear_m') || any('max_height_m') || any('max_stories')) return 'מקור קווי בניין';
  if (any('primary_use')) return 'מקור ייעוד';
  const t = p.type || '';
  if (/תמ["״']?א/.test(t)) return 'תכנית ארצית / מגבלה';
  if (/שימור/.test(t)) return 'תכנית שימור';
  if (/מתאר/.test(t)) return 'תכנית מתאר';
  if (/רובע/.test(t)) return 'תכנית רובע';
  return 'מסמך מדיניות';
}

// Relevance to the analysis (§9.5): in-force + rules = relevant; national/policy
// = a possible limit; cancelled/rejected = out of scope.
function planRelevance(p) {
  const tone = p.status?.tone;
  if (tone === 'neutral' || tone === 'bad') return { label: 'לא רלוונטית לסקופ', tone: 'neutral' };
  if (/תמ["״']?א/.test(p.type || '')) return { label: 'מגבלה אפשרית', tone: 'warn' };
  if (p.hasRules) return { label: 'רלוונטית לניתוח', tone: 'ok' };
  return { label: 'למידע', tone: 'neutral' };
}

// Build the statutory data layer from a loaded project (its chunk-2 snapshot).
export function buildStatutory(loaded) {
  const idn = loaded?.project?.identification || {};
  const identification = idn.identification || {};
  const parcel = idn.parcel || null;
  const pf = idn.parcelFacts || null;
  const plans = idn.plans || [];
  const docs = loaded?.documents || [];
  const manualSources = (idn.sources || []).filter((s) => s.manual);

  const gush = identification.gush;
  const helka = identification.helka;
  const buildIso = new Date().toISOString();

  // Rich source: label+link for the row display, plus the spec's three-tier
  // markers (database → dataset → page/section) that F() lifts onto the field.
  const src = (label, link, database, dataset, ref = {}) => ({
    label, link: link || null, database: database || null, dataset: dataset || null,
    page: ref.page ?? null, section: ref.section ?? null,
  });

  const govmapLink = parcel ? `https://www.govmap.gov.il/?c=${Math.round(parcel.coords.x)},${Math.round(parcel.coords.y)}&z=10` : null;
  const govmap = src('GovMap', govmapLink, DATABASES.govmap,
    { id: 'ds_govmap_parcel', name: 'זיהוי חלקה (GovMap)', type: 'gis_layer' });
  const bfLink = gush && helka
    ? `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${gush}_${helka}`
    : null;
  // OCR evidence page (from parcelFacts.source_urls), falling back to the file.
  const bfDataset = { id: 'ds_tik_binyan_permits', name: 'היתרים היסטוריים (OCR)', type: 'permit_document' };
  const ocr = (i = 0) => (pf?.source_urls?.[i]
    ? src('תיק בניין (OCR)', pf.source_urls[i], DATABASES.buildingFile, bfDataset)
    : (bfLink ? src('תיק בניין', bfLink, DATABASES.buildingFile, bfDataset) : null));

  let seq = 0;
  // Field factory: a present value gets its source/confidence/status; a null
  // value becomes a gap. The gap flavor follows §14: opts.missing='source' marks
  // a Missing Source (the dataset that would carry it wasn't collected —
  // opts.expectedSource names it for the CTA); default is Missing Field.
  const F = (groupId, name, value, opts = {}) => {
    const has = value !== null && value !== undefined && String(value).trim() !== '';
    const s = has ? (opts.source || null) : null;
    return {
      id: `${groupId}-${seq++}`,
      fieldName: opts.key || null, // stable English key (spec §15 field_name)
      groupId,
      name,
      value: has ? String(value) : null,
      unit: opts.unit || '',
      extra: opts.extra || null, // small display tag (e.g. a plan's status)
      source: s ? { label: s.label, link: s.link || null } : null,
      database: s?.database || null,
      dataset: s?.dataset || null,
      sourceRef: s ? { link: s.link || null, page: s.page ?? null, section: s.section ?? null } : null,
      confidence: has ? (opts.confidence || 'medium') : 'missing',
      // How the value answers the question (tachsit-log rule 4): 'explicit' — a
      // number/text stated by the source; 'geometric_rule' — the value is a
      // calculation method (e.g. building-lines envelope); 'conditional_cap' —
      // a ceiling that applies only under a stated predicate; 'computed' —
      // derived by us (e.g. polygon area). Rules are answers, not gaps.
      valueKind: opts.valueKind || 'explicit',
      status: has ? (opts.status || 'check') : (opts.missing === 'source' ? 'missing_source' : 'missing'),
      // The gap flavor this field returns to when cleared/restored (§14).
      missingKind: opts.missing === 'source' ? 'missing_source' : 'missing',
      expectedSource: opts.expectedSource || null, // where the gap would come from
      candidates: opts.candidates || null, // alternative values from other sources (§14)
      conflict: opts.conflict || false,
      systemRecommendation: opts.systemRecommendation || null, // {value, reasonHe} (§17)
      userResolution: null, // set when the user resolves a conflict (§17)
      originalValue: null, // preserved on edit/override
      note: '',
      inScope: true,
      history: [], // audit trail — appended on every user action, never truncated
      lastUpdated: buildIso,
    };
  };

  // Map any land-use label (Hebrew or the KB's English codes) to a coarse use
  // category, so we can tell which of a city-wide plan's zones apply to THIS
  // parcel. Returns null when unclassifiable (then we don't filter on it).
  const USE_CAT = (s) => {
    const t = String(s || '').toLowerCase();
    if (!t) return null;
    const resid = /מגור|resid/.test(t);
    const comm = /תעסוק|מסחר|משרד|commerc|office|retail/.test(t);
    // A zone naming BOTH residential and commercial is mixed — it must not be read
    // as residential (that's what let "מסחר ומגורים" leak into a מגורים parcel).
    if ((resid && comm) || /מעורב|mixed/.test(t)) return 'mixed';
    if (/תשתי|הנדסי|infrastructure/.test(t)) return 'infrastructure';
    if (resid) return 'residential';
    if (/מלונ|hotel/.test(t)) return 'hospitality';
    if (/תעשי|מלאכ|industr/.test(t)) return 'industrial';
    if (/מוסד|ציבור|public/.test(t)) return 'public';
    if (/פתוח|open_space|שצ״?פ|גינ|פארק/.test(t)) return 'open_space';
    if (comm) return 'commercial';
    return null;
  };
  // Overlay / supplemental zones (roof construction, roof additions, dwelling
  // enlargements, basements, pergolas) carry their OWN setbacks/heights that must
  // not be compared against a base plot's envelope — e.g. plan ג's roof side-line
  // of 1.2m isn't the parcel's side building line. Excluded from base-envelope
  // params, but still counted for permitted uses.
  const IS_OVERLAY = (name) => /גג|עליי[תה]|תוספת|הרחב|מרתף|פרגול/.test(String(name || ''));

  // The parcel's authoritative land use (GIS 837): its specific zone (yeud_karka,
  // e.g. מגורים ב) and use category. This SCOPES the KB zone rules — a city-wide /
  // rova plan carries a full zoning table (residential, hotel, offices, industry…)
  // and only the parcel's own zone applies, so pulling every zone's value produced
  // dozens of phantom "conflicts".
  const landUse = loaded?.landUse || null;
  const dm = loaded?.dafMeyda || null; // TA daf meyda (see the DA helper below)
  const parcelCat = USE_CAT(landUse?.yeud_rashi) || USE_CAT(landUse?.yeud_karka) || USE_CAT(dm?.land_use?.zone);

  // KB zoning rules (chunk 03 B): flatten the (zone, plan) pairs from the
  // applicable plans that have extracted rules — but only zones whose use category
  // matches the parcel (when we know it), so unrelated zones don't masquerade as
  // conflicting candidates.
  const zoneSources = [];
  let zonesTotal = 0;
  let zonesKept = 0;
  for (const p of plans) {
    for (const z of (p.zones || [])) {
      zonesTotal += 1;
      if (parcelCat) {
        const zc = USE_CAT(z.primary_use) || USE_CAT(z.zone_name || z.zone);
        if (zc && zc !== parcelCat) continue; // out-of-scope zone — skip it
      }
      zonesKept += 1;
      zoneSources.push({ zone: z, plan: p });
    }
  }
  const confFrom = (c) => (c == null ? 'medium' : (c >= 0.75 ? 'high' : (c >= 0.5 ? 'medium' : 'low')));
  // Normalize a value for conflict DEDUP (numbers compare equal regardless of
  // formatting: "4", "4.0", "4 מ׳" → "4"), while the display keeps the original.
  const normKey = (v) => {
    const s = String(v).trim();
    const m = s.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (m && /^[^֐-׿]*-?\d/.test(s)) return String(Number(m[0]));
    // Text: drop geresh/gershayim/quotes and collapse spaces so תמ״א 38 == תמא 38.
    return s.replace(/[״׳"'`]/g, '').replace(/\s+/g, ' ');
  };
  // Build a field for one zone parameter by collecting its distinct values across
  // all applicable plans. One value → a plain field; several → a §14 conflict;
  // none → a gap. Each candidate carries its plan + evidence-page source.
  const dnum = (d) => { const t = d ? new Date(d).getTime() : 0; return Number.isNaN(t) ? 0 : t; };
  const cRank = { high: 3, medium: 2, low: 1, missing: 0 };
  // A plan's takanon as a Dataset (spec §8) — the zone rules were extracted from it.
  const planDs = (plan) => ({
    id: `ds_takanon_${plan.number || 'plan'}`,
    name: `תקנון תכנית ${plan.number || ''}`.trim(),
    type: 'plan_takanon',
  });
  const planSrc = (plan, zone) => src(plan.number || 'תכנית', zone.source_pdf_url || plan.planUrl || null, DATABASES.xplan, planDs(plan));
  const zoneField = (groupId, name, key, opts = {}) => {
    // Base-envelope params exclude overlay zones (roof/addition rules aren't the
    // parcel's base envelope).
    const sources = opts.baseOnly
      ? zoneSources.filter(({ zone }) => !IS_OVERLAY(zone.zone_name || zone.zone))
      : zoneSources;
    // List-type parameters (national-plan refs, special clauses, permitted uses)
    // AGGREGATE across the applicable plans — they all apply, so it's a union, not
    // a set of competing alternatives.
    if (opts.union) {
      const maxLen = opts.maxLen || 120;
      // Pull a display string out of whatever shape the KB stored (string, list,
      // or an object like { notes } / { clause }).
      const toText = (it) => {
        if (it == null) return '';
        if (typeof it === 'string') return it;
        if (typeof it === 'object') return it.text || it.notes || it.clause || it.name || it.value || '';
        return String(it);
      };
      const items = new Map(); // normalized -> { text, source }
      for (const { zone, plan } of sources) {
        const raw = zone[key];
        if (raw == null || raw === '') continue;
        let list;
        if (Array.isArray(raw)) list = raw;
        else if (typeof raw === 'object') list = [raw];
        else list = String(raw).split(/[,;]/);
        for (const it of list) {
          // Kind-tagged clauses (height_rule / basement_line_rule…) are routed to
          // their dedicated fields — keep them out of the generic union.
          if (it && typeof it === 'object' && it.kind) continue;
          const text = toText(it).trim().slice(0, maxLen);
          if (!text) continue;
          const k = normKey(text);
          if (!items.has(k)) {
            items.set(k, { text, source: planSrc(plan, zone) });
          }
        }
      }
      const all = [...items.values()];
      if (!all.length) return F(groupId, name, null, { unit: opts.unit, key: opts.key, expectedSource: opts.expectedSource });
      return F(groupId, name, all.map((x) => x.text).join(opts.bullet ? ' • ' : ', '), {
        unit: opts.unit, confidence: 'medium', key: opts.key,
        source: all.length === 1 ? all[0].source : { ...all[0].source, label: `${all.length} מקורות` },
      });
    }

    const byKey = new Map();
    for (const { zone, plan } of sources) {
      let v = zone[key];
      if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
      v = opts.fmt ? opts.fmt(v) : (Array.isArray(v) ? v.join(', ') : String(v));
      v = String(v).slice(0, 240);
      const k = normKey(v); // dedup by normalized value (formatting-insensitive)
      if (!byKey.has(k)) {
        byKey.set(k, {
          value: v, unit: opts.unit || '',
          source: planSrc(plan, zone),
          confidence: confFrom(zone.extraction_confidence),
          planNumber: plan.number || null,
          planName: plan.name && plan.name !== '—' ? plan.name : null,
          planDate: plan.date || null,
          zoneName: zone.zone_name || zone.zone || null, // the sub-zone this value belongs to
        });
      }
    }
    const cands = [...byKey.values()].sort((a, b) => (dnum(b.planDate) - dnum(a.planDate)) || (cRank[b.confidence] - cRank[a.confidence]));
    if (cands.length === 0) return F(groupId, name, null, { unit: opts.unit, key: opts.key, expectedSource: opts.expectedSource });
    if (cands.length === 1) return F(groupId, name, cands[0].value, { unit: opts.unit, key: opts.key, source: cands[0].source, confidence: cands[0].confidence });
    // Two kinds of conflict need different UX (spec §17 Conflict Record):
    //  • cross-ZONE — the values belong to different sub-zones (e.g. rova-3's low
    //    vs urban residential). This isn't supersession; the user must pick the
    //    sub-zone matching the parcel's land use, so there's NO system
    //    recommendation and we surface the zone names.
    //  • cross-PLAN — same/unknown zone across plans; newest plan likely governs,
    //    so the top-ranked candidate becomes the system recommendation (with its
    //    reason) — flagged, never auto-selected.
    const distinctZones = new Set(cands.map((c) => c.zoneName).filter(Boolean));
    const crossZone = distinctZones.size > 1;
    const candidates = crossZone ? cands : cands.map((c, i) => (i === 0 ? { ...c, likelyGoverning: true } : c));
    return {
      ...F(groupId, name, cands[0].value, { unit: opts.unit, key: opts.key }),
      value: null, source: null, confidence: 'missing', status: 'check', conflict: true,
      conflictKind: crossZone ? 'zone' : 'plan',
      parcelZone: landUse?.yeud_karka || dm?.land_use?.zone || null,
      systemRecommendation: crossZone ? null : {
        value: cands[0].value,
        reasonHe: `תכנית ${cands[0].planNumber || ''} היא העדכנית ביותר מבין המקורות — תכנית מאוחרת גוברת ככלל על קודמותיה`.trim(),
      },
      candidates,
    };
  };

  // The TA daf meyda (מידע מוקדם) — the city's authoritative rights report,
  // a Database with internal section Datasets (spec §6). Where it provides a
  // value, it wins (high confidence, single source, no conflict — the city
  // already resolved which plan governs). `DA` builds such a field; it returns
  // null when the daf meyda has no value, so callers fall back.
  const dmLink = (gush && helka)
    ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${gush}&helka=${helka}&opt=2&status=0&street=0&house=0`
    : null;
  const DM_DS = {
    land: { id: 'ds_rights_sheet_land', name: 'פרטי קרקע (דף זכויות)', type: 'rights_sheet_section' },
    rights: { id: 'ds_rights_sheet_rights', name: 'פירוט זכויות (דף זכויות)', type: 'rights_sheet_section' },
    landuse: { id: 'ds_rights_sheet_landuse', name: 'ייעוד קרקע (דף זכויות)', type: 'rights_sheet_section' },
    alerts: { id: 'ds_rights_sheet_alerts', name: 'התראות ומגבלות (דף זכויות)', type: 'rights_sheet_section' },
    plans: { id: 'ds_rights_sheet_plans', name: 'תכניות בתוקף (דף זכויות)', type: 'rights_sheet_section' },
  };
  const dmSrc = (dsKey = 'land') => src('דף זכויות (עיריית ת״א)', dmLink, DATABASES.rightsSheet, DM_DS[dsKey], { section: DM_DS[dsKey].name });
  const DA = (groupId, name, value, opts = {}) => {
    if (value == null || value === '' || (Array.isArray(value) && !value.length)) return null;
    const v = Array.isArray(value) ? value.join(', ') : String(value);
    return F(groupId, name, v, { unit: opts.unit, key: opts.key, source: dmSrc(opts.ds || 'land'), confidence: 'high' });
  };

  // GIS constraint layers as Datasets (spec §5) — each `LU`-derived field names
  // the specific layer it came from.
  const GIS_DS = {
    landuse: { id: 'ds_gis_landuse_837', name: 'שכבת ייעודי קרקע (837)', type: 'gis_layer' },
    conservation: { id: 'ds_gis_conservation', name: 'שכבות שימור (682/687)', type: 'gis_layer' },
    trees: { id: 'ds_gis_trees', name: 'שכבות עצים (628/574)', type: 'gis_layer' },
    easements: { id: 'ds_gis_easements', name: 'שכבות זיקות הנאה (749/750)', type: 'gis_layer' },
    noise: { id: 'ds_gis_noise', name: 'שכבת מפלסי רעש (786)', type: 'gis_layer' },
    transit: { id: 'ds_gis_transit', name: 'שכבות רק״ל — הקו הירוק (761/764)', type: 'gis_layer' },
  };
  const luSrc = (dsKey = 'landuse') => src('GIS עירוני ת״א', govmap.link, DATABASES.gis, GIS_DS[dsKey]);
  const LU = (groupId, name, value, opts = {}) => {
    if (value == null || value === '') return null;
    return F(groupId, name, String(value), { unit: opts.unit, key: opts.key, source: luSrc(opts.ds || 'landuse'), confidence: 'high', extra: opts.extra });
  };

  const groups = [];
  const G = (id, title, fields) => groups.push({ id, title, order: groups.length + 1, isOpen: false, fields });

  // 1 — property identification. This is the ONE group that carries the user's
  // own confirmation from chunk 2 (they identified the property and clicked הבא),
  // so it starts approved. Everything the agent extracts below starts as "needs
  // review" — the agent never self-approves those.
  // תת-חלקה isn't universal — a parcel with no sub-parcel shouldn't count as a
  // gap. Show it as "not applicable" (excluded from the missing list) instead.
  const subParcelField = identification.subParcel
    ? F('id', 'תת-חלקה', identification.subParcel, { key: 'sub_parcel', source: govmap, confidence: 'high', status: 'approved' })
    : { ...F('id', 'תת-חלקה', 'לא רלוונטי', { key: 'sub_parcel' }), value: 'לא רלוונטי', status: 'not_in_scope', inScope: false, confidence: 'missing', source: null, note: 'לא קיימת תת-חלקה לנכס זה' };
  G('id', 'זיהוי הנכס', [
    F('id', 'כתובת', identification.address, { key: 'full_address', source: govmap, confidence: 'high', status: 'approved' }),
    F('id', 'גוש', gush, { key: 'gush', source: govmap, confidence: 'high', status: 'approved' }),
    F('id', 'חלקה', helka, { key: 'helka', source: govmap, confidence: 'high', status: 'approved' }),
    subParcelField,
    F('id', 'קואורדינטות (ITM)', identification.coordinates ? `${identification.coordinates.x}, ${identification.coordinates.y}` : null, { key: 'coordinates_itm', source: govmap, confidence: 'high', status: 'approved' }),
    F('id', 'מקור זיהוי', identification.mapSource, { key: 'identification_source', source: govmap, confidence: 'high', status: 'approved' }),
  ]);

  // 2 — parcel / plot. שטח מגרש can come from TWO sources — the GIS polygon
  // (GovMap, computed) and the building file (OCR). When they disagree we surface
  // the conflict and let the user choose (§14).
  const ocrArea = pf?.parcel_area_m2 != null ? Math.round(Number(pf.parcel_area_m2)) : null;
  const gisAreaRaw = parcel?.geometry?.rings ? polygonArea(parcel.geometry.rings) : null;
  const gisArea = gisAreaRaw ? Math.round(gisAreaRaw) : null;
  const gisSource = src('GovMap · GIS', govmap.link, DATABASES.govmap,
    { id: 'ds_govmap_geometry', name: 'גאומטריית חלקה (GovMap)', type: 'gis_layer' });
  const dmArea = dm?.parcel?.area_registered_m2 != null ? Math.round(Number(dm.parcel.area_registered_m2)) : null;
  let areaField;
  if (dmArea) {
    // Multi-source datum (spec §3/§17): the daf zchuyot registered area is the
    // recommended leading source, applied but presented as a resolved Conflict
    // Record — all candidate values + the recommendation reason stay visible.
    const cands = [
      { value: String(dmArea), unit: 'מ״ר', source: dmSrc('land'), confidence: 'high' },
      gisArea ? { value: String(gisArea), unit: 'מ״ר', source: gisSource, confidence: 'high' } : null,
      ocrArea ? { value: String(ocrArea), unit: 'מ״ר', source: ocr(0), confidence: 'medium' } : null,
    ].filter(Boolean);
    areaField = F('parcel', 'שטח מגרש', dmArea, {
      unit: 'מ״ר', key: 'lot_area', source: dmSrc('land'), confidence: 'high',
      candidates: cands.length > 1 ? cands : null,
      systemRecommendation: cands.length > 1 ? {
        value: String(dmArea),
        reasonHe: 'שטח לחישוב זכויות מתוך דף הזכויות הוא המקור המוביל לחישוב בשלב זה',
      } : null,
    });
  } else if (gisArea && ocrArea) {
    const conflict = Math.abs(gisArea - ocrArea) / Math.max(gisArea, ocrArea) > 0.05;
    areaField = F('parcel', 'שטח מגרש', gisArea, {
      unit: 'מ״ר', key: 'lot_area', source: gisSource, confidence: 'high', conflict, valueKind: 'computed',
      candidates: [
        { value: String(gisArea), unit: 'מ״ר', source: gisSource, confidence: 'high' },
        { value: String(ocrArea), unit: 'מ״ר', source: ocr(0), confidence: 'medium' },
      ],
      systemRecommendation: {
        value: String(gisArea),
        reasonHe: 'המדידה הגרפית מה-GIS ודאית יותר מערך שחולץ ב-OCR מהיתרים היסטוריים',
      },
    });
  } else {
    areaField = F('parcel', 'שטח מגרש', ocrArea ?? gisArea, {
      unit: 'מ״ר', key: 'lot_area',
      source: ocrArea != null ? ocr(0) : gisSource,
      confidence: ocrArea != null ? 'medium' : 'high',
    });
  }
  // גבולות מגרש — we already hold the GIS plot polygon (from identification), so
  // describe it (vertex count + bounding-box extent) rather than flag it missing.
  const bbox = (() => {
    const r = parcel?.geometry?.rings?.[0];
    if (!r || r.length < 3) return null;
    let minx = Infinity; let miny = Infinity; let maxx = -Infinity; let maxy = -Infinity;
    for (const [px, py] of r) { if (px < minx) minx = px; if (px > maxx) maxx = px; if (py < miny) miny = py; if (py > maxy) maxy = py; }
    return { vertices: r.length, dx: Math.round(maxx - minx), dy: Math.round(maxy - miny) };
  })();
  G('parcel', 'מגרש / חלקה', [
    areaField,
    // §11 — the plan-assigned lot number (from the GIS lots layer; often equals
    // the helka in un-reparceled areas, but it's a distinct datum).
    LU('parcel', 'מספר מגרש (לפי תכנית)', landUse?.migrash, { key: 'plan_lot_number' })
      || F('parcel', 'מספר מגרש (לפי תכנית)', null, { key: 'plan_lot_number', missing: 'source', expectedSource: 'GIS עירוני · שכבת ייעודי קרקע' }),
    zoneField('parcel', 'שטח מגרש מינימלי (תב״ע)', 'min_lot_area_m2', { unit: 'מ״ר', key: 'min_lot_area', baseOnly: true }),
    DA('parcel', 'רוחב דרך', dm?.parcel?.road_width_m, { unit: 'מ׳', key: 'road_width' }) || F('parcel', 'רוחב דרך', null, { key: 'road_width', missing: 'source', expectedSource: 'דף זכויות עיריית ת״א' }),
    // פוליגון מגרש (graphic-log §5.1) — LAST in the group: the full geometry,
    // rendered graphically in the row (FieldRow draws field.polygon) with the
    // coordinates available on demand, not just a verbal description.
    parcel?.geometry?.rings && bbox
      ? {
          ...F('parcel', 'פוליגון מגרש', `${bbox.vertices} קדקודים · תיחום ${bbox.dx}×${bbox.dy} מ׳${gisArea ? ` · ${gisArea} מ״ר (חישוב גרפי)` : ''}`, { key: 'lot_polygon', source: gisSource, confidence: 'high', valueKind: 'computed' }),
          polygon: parcel.geometry.rings,
        }
      : F('parcel', 'פוליגון מגרש', null, { key: 'lot_polygon', missing: 'source', expectedSource: 'GovMap · גאומטריית חלקה' }),
  ]);

  // Overlay-plan envelope rules (spec §12 קו בניין לגג / למרתף, נסיגות גג): the
  // roof/basement overlay zones we EXCLUDE from the base envelope carry their own
  // setbacks/heights — surfaced here as their own fields, per overlay kind.
  const overlaySummary = (re) => {
    const parts = [];
    let s = null;
    for (const { zone, plan } of zoneSources) {
      const nm = zone.zone_name || zone.zone || '';
      if (!IS_OVERLAY(nm) || !re.test(nm)) continue;
      const bits = [];
      if (zone.setback_front_m != null && zone.setback_front_m !== '') bits.push(`קדמי ${zone.setback_front_m} מ׳`);
      if (zone.setback_rear_m != null && zone.setback_rear_m !== '') bits.push(`אחורי ${zone.setback_rear_m} מ׳`);
      if (zone.setback_side_m != null && zone.setback_side_m !== '') bits.push(`צדדי ${zone.setback_side_m} מ׳`);
      if (zone.max_height_m != null && zone.max_height_m !== '') bits.push(`גובה ${zone.max_height_m} מ׳`);
      if (!bits.length) continue;
      parts.push(`${String(nm).slice(0, 44)}: ${bits.join(' · ')}`);
      if (!s) s = planSrc(plan, zone);
    }
    return parts.length ? { text: parts.join(' • '), source: s } : null;
  };
  const roofLines = overlaySummary(/גג|עליי/);
  const basementLines = overlaySummary(/מרתף|פרטר/);

  // Kind-tagged textual rules extracted from the plan takanons (stored in the
  // KB zone rows' special_clauses with a `kind`): where a parameter isn't a
  // number in any plan (e.g. rova-3 defines height per-floor, plan ע defines
  // the basement line relative to the floor above), the rule TEXT is the value.
  const clauseFromZones = (kind) => {
    for (const { zone, plan } of zoneSources) {
      const sc = zone.special_clauses;
      const items = Array.isArray(sc) ? sc : (sc ? [sc] : []);
      const hit = items.find((c) => c && typeof c === 'object' && c.kind === kind && (c.notes || c.text));
      if (hit) return { text: String(hit.notes || hit.text).slice(0, 400), source: planSrc(plan, zone), plan };
    }
    return null;
  };

  // 3 — building lines & envelope. Populated from the applicable plans' zone
  // rules (setbacks / height / stories); conflicts surface when plans disagree.
  // Per-direction building lines from the daf meyda (schema v3) when the report
  // states them; the report's single general קו בניין only feeds the front line.
  // Hoisted to consts — the envelope computation (§5.2) reads their values too.
  const dmLines = dm?.parcel?.building_lines || null;
  const frontLineField = DA('lines', 'קו בניין קדמי', dmLines?.front_m ?? dm?.parcel?.building_line_m, { unit: 'מ׳', key: 'front_building_line' }) || zoneField('lines', 'קו בניין קדמי', 'setback_front_m', { unit: 'מ׳', key: 'front_building_line', baseOnly: true });
  const rearLineField = DA('lines', 'קו בניין אחורי', dmLines?.rear_m, { unit: 'מ׳', key: 'rear_building_line' }) || zoneField('lines', 'קו בניין אחורי', 'setback_rear_m', { unit: 'מ׳', key: 'rear_building_line', baseOnly: true });
  const sideLineField = DA('lines', 'קו בניין צדדי', dmLines?.side_m, { unit: 'מ׳', key: 'side_building_line' }) || zoneField('lines', 'קו בניין צדדי', 'setback_side_m', { unit: 'מ׳', key: 'side_building_line', baseOnly: true });
  G('lines', 'קווי בניין ומעטפת בנייה', [
    frontLineField,
    rearLineField,
    sideLineField,
    DA('lines', 'מספר קומות מותר', dm?.rights?.max_stories, { key: 'max_stories', ds: 'rights' }) || zoneField('lines', 'מספר קומות מותר', 'max_stories', { key: 'max_stories', baseOnly: true }),
    (() => {
      // Numeric height from the zone rules when a plan states one; otherwise the
      // takanon's height REGIME (per-floor cap, total = sum of floors) is the value.
      const zf = zoneField('lines', 'גובה בנייה מותר', 'max_height_m', { unit: 'מ׳', key: 'max_height', baseOnly: true, expectedSource: 'תקנון תכנית (XPlan)' });
      if (zf.value != null || zf.conflict) return zf;
      const rule = clauseFromZones('height_rule');
      return rule
        ? F('lines', 'גובה בנייה מותר', rule.text, { key: 'max_height', source: rule.source, confidence: 'medium', valueKind: 'geometric_rule' })
        : zf;
    })(),
    roofLines
      ? F('lines', 'נסיגות וקווי בניין לגג', roofLines.text, { key: 'roof_building_lines', source: roofLines.source, confidence: 'medium', valueKind: 'geometric_rule' })
      : F('lines', 'נסיגות וקווי בניין לגג', null, { key: 'roof_building_lines', missing: 'source', expectedSource: 'תקנון תכניות הגגות (ג/ג1)' }),
    (() => {
      if (basementLines) return F('lines', 'קו בניין למרתף', basementLines.text, { key: 'basement_building_line', source: basementLines.source, confidence: 'medium', valueKind: 'geometric_rule' });
      const rule = clauseFromZones('basement_line_rule');
      return rule
        ? F('lines', 'קו בניין למרתף', rule.text, { key: 'basement_building_line', source: rule.source, confidence: 'medium', valueKind: 'geometric_rule' })
        : F('lines', 'קו בניין למרתף', null, { key: 'basement_building_line', missing: 'source', expectedSource: 'תקנון תכנית המרתפים (ע1)' });
    })(),
  ]);

  // 4 — land use & permitted uses (from the zones' primary/secondary uses).
  // Land use is a single per-parcel fact — the GIS 837 yeud is authoritative
  // (e.g. מגורים ב), with the daf meyda / plan rules as fallback. Permitted uses
  // aggregate (union) across the applicable plans, scoped to the parcel's zone.
  const luGoverning = landUse?.governing_plan ? { extra: `נקבע בתב״ע ${landUse.governing_plan}` } : {};
  G('landuse', 'ייעוד קרקע ושימושים', [
    LU('landuse', 'ייעוד קרקע', landUse?.yeud_karka, { ...luGoverning, key: 'land_use_zone' })
      || DA('landuse', 'ייעוד קרקע', dm?.land_use?.zone, { key: 'land_use_zone', ds: 'landuse' })
      || zoneField('landuse', 'ייעוד קרקע', 'primary_use', { key: 'land_use_zone' }),
    LU('landuse', 'שימוש עיקרי', landUse?.yeud_rashi, { key: 'primary_use' })
      || DA('landuse', 'שימוש עיקרי', dm?.land_use?.primary_use, { key: 'primary_use', ds: 'landuse' }),
    DA('landuse', 'תכליות מותרות', (dm?.permitted_purposes || []).slice(0, 15), { key: 'permitted_purposes', ds: 'landuse' }),
    zoneField('landuse', 'שימושים מותרים', 'secondary_uses', { union: true, key: 'permitted_uses' }),
  ].filter(Boolean));

  // 5 — applicable plans (§9.5): each plan carries number, name, type, status,
  // primary role, relevance, source, and whether it needs further analysis.
  const planFields = plans.length
    ? plans.map((p) => ({
        ...F('plans', p.number || 'תכנית', p.name || '—', {
          key: `plan_${String(p.number || 'unknown').replace(/[^0-9A-Za-zא-ת]/g, '_')}`,
          source: src(p.number || 'תכנית', p.planUrl || null, DATABASES.xplan, planDs(p)),
          confidence: 'high',
        }),
        kind: 'plan',
        planType: p.type && p.type !== '—' ? p.type : 'תכנית',
        statusLabel: p.status?.label || '—',
        statusTone: p.status?.tone || 'neutral',
        depositDate: p.depositDate || null, // תאריך הפקדה
        onlineNumber: p.onlineNumber || null, // מספר מקוון
        role: planPrimaryRole(p),
        relevance: planRelevance(p),
        needsFurther: Boolean(p.hasRules) && (p.status?.tone === 'ok'),
      }))
    : [F('plans', 'תכניות חלות', null)];
  // Plans still in planning (deposited / published per clauses 77-78, not yet
  // approved) and policy documents — from the daf zchuyot (spec §6, §11).
  // Decorated as plan-kind rows so they align with the plans-table columns.
  const planningFields = (dm?.plans_in_planning || [])
    .filter((p) => p && (p.number || p.name))
    .map((p, i) => ({
      ...F('plans', p.number || p.name, p.name && p.name !== p.number ? p.name : 'תכנית בהליכי תכנון', {
        key: `planning_${i}`, source: dmSrc('plans'), confidence: 'high',
      }),
      kind: 'plan',
      planType: 'בהליכי תכנון',
      statusLabel: [p.stage, p.date].filter(Boolean).join(' · ') || 'בתכנון',
      statusTone: 'warn',
      role: 'תכנית עתידית — למעקב',
      relevance: { label: 'למעקב', tone: 'warn' },
      needsFurther: false,
    }));
  const policyFields = (dm?.policy_docs || [])
    .filter((p) => p && (p.number || p.name))
    .map((p, i) => ({
      ...F('plans', p.number || p.name, [p.name && p.name !== p.number ? p.name : null, p.topic].filter(Boolean).join(' · ') || 'מסמך מדיניות', {
        key: `policy_${i}`, source: dmSrc('plans'), confidence: 'high',
      }),
      kind: 'plan',
      planType: 'מסמך מדיניות',
      statusLabel: p.decision_date || 'מדיניות',
      statusTone: 'neutral',
      role: 'מסמך מדיניות',
      relevance: { label: 'למידע', tone: 'neutral' },
      needsFurther: false,
    }));
  G('plans', 'תכניות חלות ותפקיד ראשוני', [...planFields, ...planningFields, ...policyFields]);

  // 6 — building-rights parameters FOUND (not the final calculation). Allowed
  // FAR / stories / density / main+service areas from the daf zchuyot's פירוט
  // זכויות section (spec §12 lists שטח עיקרי/שירות מותר as extractable Data
  // Fields), with the plans' zone rules as fallback. When the daf zchuyot states
  // the right as a formula/reference instead of a number, the note is the value.
  const dmBasement = dm?.rights?.basement_pct != null
    ? `${dm.rights.basement_pct}%${dm.rights.basement_plan ? ` (${dm.rights.basement_plan})` : ''}`
    : null;
  const dmMainArea = dm?.rights?.main_area_sqm != null ? dm.rights.main_area_sqm : (dm?.rights?.main_area_note || null);
  const dmServiceArea = dm?.rights?.service_area_sqm != null ? dm.rights.service_area_sqm : (dm?.rights?.service_area_note || null);
  // אחוז בנייה — resolved the way an architect resolves it. When the governing
  // detailed plan (rova) replaces FAR-% with an envelope regime (a far_rule
  // clause), the older/mitar candidates aren't real alternatives: a תכנית מתאר
  // כוללנית doesn't confer permit-level rights, and the rova plan cancels prior
  // unusable rights. The rule text becomes the value; the candidates stay
  // visible, each annotated with WHY it doesn't govern.
  const farField = (() => {
    // Schema v3 splits אחוזי בנייה (far_pct) from תכסית (coverage_pct) — the
    // FAR field must never be fed from a coverage value again.
    const fromDm = DA('rights', 'אחוז בנייה מותר', dm?.rights?.far_pct, { unit: '%', key: 'max_building_pct', ds: 'rights' });
    if (fromDm) return fromDm;
    const zf = zoneField('rights', 'אחוז בנייה מותר (תב״ע)', 'max_far_pct', { unit: '%', key: 'max_building_pct', baseOnly: true });
    const rule = clauseFromZones('far_rule');
    if (!rule || (!zf.conflict && zf.value != null)) return zf;
    const ruleDate = dnum(rule.plan?.date);
    const annotated = (zf.candidates || []).map((c) => {
      const isMitar = /מתאר/.test(c.planName || '');
      const older = ruleDate && dnum(c.planDate) && dnum(c.planDate) < ruleDate;
      return {
        ...c,
        disqualifier: isMitar
          ? 'תכנית מתאר כוללנית — אינה מקנה זכויות למתן היתר'
          : (older ? `זכויות תכניות קודמות שלא נוצלו בוטלו בתכנית ${rule.plan?.number || 'הרובע'} (ס׳ 5)` : null),
      };
    });
    return {
      ...F('rights', 'אחוז בנייה מותר', rule.text, { key: 'max_building_pct', source: rule.source, confidence: 'medium', valueKind: 'geometric_rule' }),
      conflict: true, conflictKind: 'plan', candidates: annotated,
      systemRecommendation: {
        value: rule.text,
        reasonHe: `תכנית ${rule.plan?.number || 'הרובע'} היא התכנית הקובעת: היא מבטלת זכויות קודמות שלא נוצלו, ותכנית מתאר כוללנית אינה מקנה זכויות להיתר — ולכן הערכים המספריים המוצגים אינם חלים. ברובע אין אחוז בנייה נקוב; הזכויות נגזרות מהמעטפת.`,
      },
      userResolution: null,
    };
  })();
  G('rights', 'זכויות בנייה — פרמטרים שנמצאו', [
    farField,
    DA('rights', 'מקדם צפיפות', dm?.rights?.density_coeff, { key: 'density_coeff', ds: 'rights' }) || F('rights', 'מקדם צפיפות', null, { key: 'density_coeff', missing: 'source', expectedSource: 'דף זכויות עיריית ת״א' }),
    DA('rights', 'מספר קומות מותר', dm?.rights?.max_stories, { key: 'max_stories_rights', ds: 'rights' }) || zoneField('rights', 'מספר קומות מותר', 'max_stories', { key: 'max_stories_rights', baseOnly: true }),
    DA('rights', 'מרתף', dmBasement, { key: 'basement_rights', ds: 'rights' }) || F('rights', 'מרתף', null, { key: 'basement_rights', missing: 'source', expectedSource: 'דף זכויות עיריית ת״א' }),
    DA('rights', 'שטח עיקרי מותר', dmMainArea, { unit: dm?.rights?.main_area_sqm != null ? 'מ״ר' : '', key: 'main_area_allowed', ds: 'rights' })
      || F('rights', 'שטח עיקרי מותר', null, { unit: 'מ״ר', key: 'main_area_allowed', missing: 'source', expectedSource: 'דף זכויות · פירוט זכויות / תקנון תכנית' }),
    DA('rights', 'שטח שירות מותר', dmServiceArea, { unit: dm?.rights?.service_area_sqm != null ? 'מ״ר' : '', key: 'service_area_allowed', ds: 'rights' })
      || F('rights', 'שטח שירות מותר', null, { unit: 'מ״ר', key: 'service_area_allowed', missing: 'source', expectedSource: 'דף זכויות · פירוט זכויות / תקנון תכנית' }),
    // תכסית מותרת — an explicit % when the daf zchuyot states one; otherwise the
    // coverage RULE is the value (e.g. building-lines envelope with a conditional
    // 55%/750m² cap under the rova plans). A found rule is an answer, not a gap —
    // never convert a conditional rule to a bare number here. When the rule's
    // predicate depends on the lot area, we HOLD that area (daf zchuyot / GIS /
    // OCR, same order as שטח מגרש above) — so the cap is decided, not just shown.
    (() => {
      const cr = dm?.rights?.coverage_rule || null;
      const fromPct = DA('rights', 'תכסית מותרת', dm?.rights?.coverage_pct, { unit: '%', key: 'coverage_allowed', ds: 'rights' });
      if (fromPct) return { ...fromPct, valueKind: 'explicit', extra: dm?.rights?.coverage_note || null };
      if (cr && (cr.text || cr.cap_pct != null)) {
        const capTxt = cr.cap_pct != null ? `עד ${cr.cap_pct}%` : null;
        const condTxt = cr.condition_min_lot_sqm != null ? `במגרשים בשטח ${cr.condition_min_lot_sqm} מ״ר ומעלה` : null;
        const text = cr.text || [capTxt, condTxt].filter(Boolean).join(' ');
        const lotForRule = dmArea ?? gisArea ?? ocrArea;
        let verdict = null;
        let decided = false;
        if (cr.condition_min_lot_sqm != null && lotForRule != null) {
          decided = true;
          verdict = lotForRule >= cr.condition_min_lot_sqm
            ? `המגבלה חלה (${lotForRule} מ״ר ≥ ${cr.condition_min_lot_sqm}) — תכסית עד ${cr.cap_pct != null ? `${cr.cap_pct}%` : 'התקרה'} ובכפוף למעטפת קווי הבניין`
            : `המגבלה אינה חלה על מגרש זה (${lotForRule} מ״ר < ${cr.condition_min_lot_sqm}) — התכסית נגזרת ממעטפת קווי הבניין`;
        }
        // Supersession guard (tachsit-log rule 1): historic coverage values —
        // the building-file OCR percent from old permits — stay visible as
        // DISQUALIFIED candidates with the reason, never as the governing value.
        const historicCands = pf?.coverage_pct != null ? [
          {
            value: String(pf.coverage_pct),
            unit: '%',
            source: ocr(0),
            confidence: 'medium',
            disqualifier: 'תכסית היסטורית מהיתר/תיק בניין — משקפת את הבנוי בפועל ואינה קובעת תכסית מותרת לבנייה חדשה תחת תכנית הרובע',
          },
        ] : [];
        return {
          ...F('rights', 'תכסית מותרת', text, {
            key: 'coverage_allowed',
            source: dmSrc('rights'),
            confidence: decided ? 'high' : 'medium',
            valueKind: cr.condition_min_lot_sqm != null ? 'conditional_cap' : 'geometric_rule',
            extra: verdict,
          }),
          ...(historicCands.length ? {
            conflict: true,
            conflictKind: 'plan',
            candidates: historicCands,
            systemRecommendation: {
              value: text,
              reasonHe: 'תכנית הרובע קובעת את התכסית כמעטפת קווי הבניין עם תקרה מותנית — ערך היסטורי מהיתר ישן אינו קובע לבנייה חדשה',
            },
            userResolution: null,
          } : {}),
        };
      }
      return F('rights', 'תכסית מותרת', null, { key: 'coverage_allowed', missing: 'source', expectedSource: 'דף זכויות · פירוט זכויות / תקנון תכנית הרובע' });
    })(),
    // שטח בין קווי בניין (graphic-log §5.2) — the computed result the coverage
    // rule points to: minimal-area oriented bounding rectangle of the parcel,
    // inset by the building lines (side setbacks on the short axis — narrow
    // street frontage is the norm; the method is stated in the note). An
    // ESTIMATE, labeled as such — never presented as a surveyed figure.
    (() => {
      const numOf = (f) => { const v = parseFloat(f?.value); return Number.isFinite(v) ? v : null; };
      const obb = parcel?.geometry?.rings ? minAreaRect(parcel.geometry.rings) : null;
      const fr = numOf(frontLineField);
      const rr = numOf(rearLineField);
      const sd = numOf(sideLineField);
      const lotAreaForPct = dmArea ?? gisArea ?? ocrArea;
      if (!obb || fr == null || rr == null || sd == null) {
        return F('rights', 'שטח בין קווי בניין (מחושב)', null, { key: 'coverage_envelope_area', missing: 'source', expectedSource: 'פוליגון מגרש (GovMap) + קווי בניין' });
      }
      const bw = obb.w - 2 * sd;
      const bd = obb.d - fr - rr;
      if (bw <= 0 || bd <= 0) {
        return F('rights', 'שטח בין קווי בניין (מחושב)', 'קווי הבניין אינם מותירים שטח במלבן החוסם — נדרש חישוב מתשריט', { key: 'coverage_envelope_area', source: gisSource, confidence: 'low', valueKind: 'computed' });
      }
      const area = Math.round(bw * bd);
      const pct = lotAreaForPct ? Math.round((area / lotAreaForPct) * 100) : null;
      // The NUMBER is the value — everything else (percent, estimate flag,
      // method) rides as chips/note so the row reads at a glance.
      return {
        ...F('rights', 'שטח בין קווי בניין (מחושב)', `≈${area}`, {
          unit: 'מ״ר',
          key: 'coverage_envelope_area',
          source: src('חישוב אורבניקס — פוליגון × קווי בניין', null, DATABASES.govmap, { id: 'ds_computed_envelope', name: 'חישוב מעטפת קווי בניין', type: 'computed' }),
          confidence: 'medium',
          valueKind: 'computed',
          extra: pct != null ? `כ־${pct}% משטח המגרש · הערכה` : 'הערכה',
        }),
        note: `שיטה: מלבן חוסם ${Math.round(obb.w * 10) / 10}×${Math.round(obb.d * 10) / 10} מ׳ בקיזוז קווי הבניין (${fr} / ${rr} / ${sd})`,
      };
    })(),
  ]);

  // 7 — statutory limits / special layers. National-plan refs + special clauses
  // come from the zone rules; the rest remain gaps (GIS layers, later).
  // Limits from the daf meyda's alerts table (התראות) — each carries its granting
  // plan; these are authoritative so they lead the group.
  const dmLimitFields = (dm?.limits || [])
    .filter((l) => l && (l.type || l.note))
    .map((l, i) => F('limits', l.type || 'מגבלה', l.note || l.type, {
      key: `alert_${i}`,
      source: { ...dmSrc('alerts'), label: l.granting_plan || 'דף זכויות (עיריית ת״א)' },
      confidence: 'high',
    }));
  // שימור — from the GIS conservation layers (682 listed buildings, 687 areas of
  // conservation responsibility). A definitive "not listed" is an answer, not a gap.
  const cons = landUse?.conservation || null;
  const consSrc = luSrc('conservation');
  let consField;
  if (cons && cons.listed) {
    consField = F('limits', 'שימור', `מבנה מיועד לשימור${cons.building_status ? ` — ${cons.building_status}` : ''}`, { key: 'conservation', source: consSrc, confidence: 'high' });
  } else if (cons && cons.area_name) {
    consField = F('limits', 'שימור', `בתחום אזור אחריות שימור: ${cons.area_name} (המבנה אינו רשום לשימור פרטני)`, { key: 'conservation', source: consSrc, confidence: 'medium' });
  } else if (cons) {
    consField = F('limits', 'שימור', 'לא נמצא ייעוד שימור', { key: 'conservation', source: consSrc, confidence: 'high' });
  } else {
    consField = F('limits', 'שימור', null, { key: 'conservation', missing: 'source', expectedSource: 'GIS עירוני · שכבות שימור' });
  }
  // עצים / זיקות הנאה / סביבה — from the GIS layers (trees 628+574, easements
  // 749/750, transport-noise 786). A definitive "none" is an answer, not a gap.
  const ease = landUse?.easement || null;
  const env = landUse?.environmental || null;
  const trees = landUse?.trees || null;
  const treesField = trees
    ? (trees.surveyed > 0
      ? F('limits', 'עצים בוגרים', `${trees.surveyed} עצים רשומים בסקר העירוני בסביבת המגרש — ייתכן היתר כריתה`, { key: 'mature_trees', source: luSrc('trees'), confidence: 'medium' })
      : (trees.canopies > 0
        ? F('limits', 'עצים בוגרים', `אין עצים רשומים; זוהו ${trees.canopies} חופות עצים בסביבה — לאימות בשטח`, { key: 'mature_trees', source: luSrc('trees'), confidence: 'medium' })
        : F('limits', 'עצים בוגרים', 'לא נמצאו עצים רשומים בסביבת המגרש', { key: 'mature_trees', source: luSrc('trees'), confidence: 'medium' })))
    : F('limits', 'עצים בוגרים', null, { key: 'mature_trees', missing: 'source', expectedSource: 'GIS עירוני · שכבות עצים' });
  const easeField = ease
    ? (ease.present
      ? F('limits', 'זיקות הנאה', `קיימת זיקת הנאה${ease.note ? ` — ${ease.note}` : ''}`, { key: 'easements', source: luSrc('easements'), confidence: 'high' })
      : F('limits', 'זיקות הנאה', 'לא נמצאה זיקת הנאה רשומה', { key: 'easements', source: luSrc('easements'), confidence: 'high' }))
    : F('limits', 'זיקות הנאה', null, { key: 'easements', missing: 'source', expectedSource: 'GIS עירוני · שכבות זיקות הנאה / נסח טאבו' });
  const envField = env
    ? (env.noise
      ? F('limits', 'מגבלות סביבתיות', `בתחום מפלסי רעש מתחבורה${env.noise_level ? ` (מפלס ${env.noise_level}, גובה 4מ׳)` : ''}`, { key: 'environmental_limits', source: luSrc('noise'), confidence: 'medium' })
      : F('limits', 'מגבלות סביבתיות', 'לא נמצאו מגבלות רעש מתחבורה', { key: 'environmental_limits', source: luSrc('noise'), confidence: 'medium' }))
    : F('limits', 'מגבלות סביבתיות', null, { key: 'environmental_limits', missing: 'source', expectedSource: 'GIS עירוני · שכבת מפלסי רעש' });
  // מטרו / רכבת קלה (spec §12 migbalot list) — the green-line route/stations
  // layers; overlap means the parcel touches the alignment (a rights limit).
  const transit = landUse?.transit || null;
  const transitField = transit
    ? (transit.light_rail
      ? F('limits', 'מטרו / רכבת קלה', `בתחום ${transit.detail || 'תוואי מתע״ן'} — ייתכנו מגבלות בנייה`, { key: 'transit', source: luSrc('transit'), confidence: 'medium' })
      : F('limits', 'מטרו / רכבת קלה', 'המגרש אינו בתחום תוואי רק״ל מוכרז (הקו הירוק)', { key: 'transit', source: luSrc('transit'), confidence: 'medium' }))
    : F('limits', 'מטרו / רכבת קלה', null, { key: 'transit', missing: 'source', expectedSource: 'GIS עירוני · שכבות רק״ל' });
  // General notes/instructions affecting rights, outside the alerts table (§6).
  const dmNoteFields = (dm?.notes_instructions || [])
    .filter((n) => n && n.text)
    .map((n, i) => F('limits', n.plan ? `הוראה (${n.plan})` : 'הוראה כללית', String(n.text).slice(0, 280), {
      key: `instruction_${i}`,
      source: { ...dmSrc('alerts'), label: n.plan || 'דף זכויות (עיריית ת״א)' },
      confidence: 'high',
    }));
  G('limits', 'מגבלות סטטוטוריות / שכבות מיוחדות', [
    ...dmLimitFields,
    ...dmNoteFields,
    zoneField('limits', 'תכניות ארציות (תמ״א)', 'national_plan_refs', { union: true, key: 'national_plans' }),
    zoneField('limits', 'הוראות מיוחדות', 'special_clauses', { union: true, maxLen: 280, bullet: true, key: 'special_clauses' }),
    consField,
    treesField,
    easeField,
    envField,
    transitField,
  ]);

  // 8 — manually uploaded documents.
  // Each uploaded document is its own Dataset inside the project-archive
  // Database (spec §4 — "כל מסמך שמשלים נתון נשמר כאן ומשמש Dataset עצמאי").
  const docDs = (nm, i) => ({ id: `ds_doc_${i}`, name: nm, type: 'uploaded_document' });
  const docFields = [
    ...docs.map((d, i) => F('docs', d.file_name || d.document_type || 'מסמך', d.document_type || 'מסמך שהועלה', {
      key: `doc_${i}`,
      source: src('הועלה ידנית', (d.file_id && /^https?:\/\//.test(d.file_id)) ? d.file_id : null, DATABASES.archive, docDs(d.file_name || 'מסמך', i)),
      confidence: 'high',
    })),
    ...manualSources.map((s, i) => F('docs', s.fileName || s.source, 'מסמך שהועלה', {
      key: `manual_doc_${i}`,
      source: src('הועלה ידנית', s.link || null, DATABASES.archive, docDs(s.fileName || s.source || 'מסמך', `m${i}`)),
      confidence: 'high',
    })),
  ];
  G('docs', 'מסמכים שהועלו ידנית', docFields);

  // 9 — existing structure — extracted data (analysis is the NEXT screen).
  // Actual-state fields live HERE, never in the main rights/parcel groups
  // (graphic-log §6): the main data screen shows permitted/statutory values;
  // what is actually built belongs under מצב קיים.
  const exFields = [
    F('existing', 'יחידות דיור (בפועל)', pf?.units_count, { key: 'units_existing', source: ocr(0), confidence: 'medium', missing: 'source', expectedSource: 'תיק בניין / נסח טאבו' }),
    F('existing', 'תכסית קיימת (מהיתר, OCR)', pf?.coverage_pct, { unit: '%', key: 'coverage_actual', source: ocr(0), confidence: 'medium', missing: 'source', expectedSource: 'תיק בניין עירוני' }),
    ...(pf?.permits_timeline || []).map((t, i) => F('existing', `היתר ${t.year || ''}`.trim() || 'היתר', t.description, {
      key: `permit_${t.year || i}`,
      source: t.url ? src('תיק בניין (OCR)', t.url, DATABASES.buildingFile, bfDataset) : ocr(0),
      confidence: 'low',
    })),
  ];
  G('existing', 'מצב קיים — נתונים שחולצו', exFields);

  // Stage-specific agent activity log (§5.1) — what the agent did organizing this
  // data layer, so the rail reflects THIS stage, not the identification log.
  const log = [];
  let lseq = 0;
  const evt = (text, kind) => log.push({ id: `sl-${lseq++}`, time: logTime(), text, kind });
  const missingCount = groups.reduce((n, g) => n + g.fields.filter((f) => isGapStatus(f.status)).length, 0);
  evt('התחלת ניתוח מצב סטטוטורי', 'info');
  evt('חילוץ נתונים מהמקורות שנאספו', 'done');
  if (gush && helka) evt(`זוהה נכס — גוש ${gush} / חלקה ${helka}`, 'done');
  if (dm) evt(`שולב דף זכויות (מידע מוקדם) — עיריית ת״א כמקור מוסמך${dm?.issue?.issue_date ? ` · הופק ${dm.issue.issue_date}` : ''}`, 'done');
  if (landUse?.yeud_karka) evt(`ייעוד קרקע לפי GIS — ${landUse.yeud_karka}${landUse.governing_plan ? ` (תב״ע ${landUse.governing_plan})` : ''}`, 'done');
  if (parcelCat && zonesTotal > zonesKept) {
    evt(`מיקוד כללי התב״ע לייעוד הנכס — ${zonesKept} מתוך ${zonesTotal} אזורים רלוונטיים`, 'done');
  }
  if (dmArea || ocrArea || gisArea) {
    evt(areaField?.conflict ? 'שטח מגרש — נמצאו ערכים סותרים (GIS מול תיק בניין)' : 'חולצו נתוני מגרש (שטח / תכסית)', areaField?.conflict ? 'warn' : 'done');
  }
  evt(plans.length ? `סווגו ${plans.length} תכניות חלות` : 'לא נמצאו תכניות חלות', plans.length ? 'done' : 'info');
  const conflictCount = groups.reduce((n, g) => n + g.fields.filter((f) => f.conflict).length, 0);
  if (conflictCount) evt(`נותרו ${conflictCount} ערכים בבדיקה (מקורות סותרים)`, 'warn');
  evt(missingCount ? `אותרו ${missingCount} נתונים חסרים להשלמה` : 'לא נותרו חוסרים', missingCount ? 'warn' : 'done');
  evt('הנתונים סודרו לפי נושאים', 'done');

  return {
    signature: `${STAT_VERSION}|${idn.signature || ''}`,
    builtVersion: STAT_VERSION,
    hasZones: zoneSources.length > 0, // whether KB rules were available at build
    // The plan set this snapshot was built from — reuse regenerates when it
    // changes (e.g. a newly-ingested plan enters the applicable list).
    planRefs: plans.map((p) => p.number).filter(Boolean).sort().join('|'),
    generatedAt: new Date().toISOString(),
    // Cache the structured daf meyda so it's not re-fetched (~15s gpt-4o) on
    // every reuse of this snapshot. `dafMeydaTried` records that the fetch was
    // attempted — so a snapshot built BEFORE the daf meyda was wired (no attempt)
    // is treated as stale for a TA parcel and rebuilds once, while a snapshot
    // where the fetch legitimately returned nothing is still reused.
    dafMeyda: dm,
    dafMeydaTried: Boolean(loaded?.dafMeydaTried),
    // The GIS parcel land use — cached so the scoping key survives reuse.
    landUse,
    landUseTried: Boolean(loaded?.landUseTried),
    log,
    groups,
  };
}
